/**
 * Authentication Context Provider
 * Handles global auth state monitoring and anonymous user management
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from '@tabletuck/firebase-services';
import {
  loggedInSuccess,
  loggedOutSuccess,
  setUser,
  setAnonymousStatus,
  UpdateScheduledTime,
  UpdateCartData,
} from '@tabletuck/redux-slices';
import { createAnonymousUserDoc } from '@tabletuck/firebase-services';

const AuthContext = createContext({});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isLoggedIn, isAnonymous } = useSelector((state) => state.authReducer);

  useEffect(() => {
    

    // Check if auth is available
    if (!auth) {
      console.warn(
        '⚠️ [AUTH_CONTEXT] Auth not available, skipping state listener'
      );
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
     

      if (user) {
        // User is signed in (either anonymous or authenticated)
        const userIsAnonymous = user.isAnonymous;

        // Update Redux state
        dispatch(
          loggedInSuccess({
            token: user.uid,
            isAnonymous: userIsAnonymous,
          })
        );
        dispatch(setAnonymousStatus(userIsAnonymous));

        // Get or create user document
        try {
          let userData = null;

          if (userIsAnonymous) {
            // Handle anonymous user document
            // Default to 'pickup' for existing anonymous users being processed by auth state change
            const userDocResult = await createAnonymousUserDoc(
              user.uid,
              'pickup'
            );
            if (userDocResult.success) {
              userData = userDocResult.userData;
            }
          } else {
            // Handle authenticated user document
            try {
              const firestore = auth.app ? auth.app.firestore() : null;
              if (firestore) {
                const userDocRef = firestore.collection('users').doc(user.uid);
                const userDoc = await userDocRef.get();
                if (userDoc.exists) {
                  userData = userDoc.data();
                }
              }
            } catch (firestoreError) {
              console.error(
                '❌ [AUTH_CONTEXT] Error accessing Firestore:',
                firestoreError
              );
            }
          }

          if (userData) {
            dispatch(
              setUser({
                user: user,
                userData: userData,
              })
            );
          }

          if (!userIsAnonymous) {
            const saved = localStorage.getItem(`scheduledTime_${user.uid}`);
            if (saved) {
              try {
                const parsed = JSON.parse(saved);
                dispatch(UpdateScheduledTime(parsed));
                dispatch(UpdateCartData({ scheduledTime: parsed }));
              } catch (_) {}
            }
          }
        } catch (error) {
          console.error(
            '❌ [AUTH_CONTEXT] Error fetching user document:',
            error
          );
        }
      } else {
        // User is signed out — clear auth state and any scheduled time
        dispatch(loggedOutSuccess());
        dispatch(setAnonymousStatus(false));
        dispatch(UpdateScheduledTime(null));
        dispatch(UpdateCartData({ scheduledTime: null }));
      }
    });

    return () => {
      
      try {
        if (unsubscribe) {
          unsubscribe();
        }
      } catch (error) {
        console.error(
          '❌ [AUTH_CONTEXT] Error cleaning up auth listener:',
          error
        );
      }
    };
  }, [dispatch]);


  const contextValue = {
    // Add any context-specific functions here if needed
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
